<template>
  <component v-if="anyActive && reference" :is="reference"></component>

  <div v-else class="landing-sidenav">
    <div class="landing-sidenav__title">
      {{ $t('about-the-product') }}
    </div>

    <Content
      :children="containers(['Section', 'Tabs'])"
      :resizeable="false"
      @positionChange="positionChanged"
      @click="setActive($event)"
      @delete="removeSection({ id: $event })"
    />

    <Divider />

    <div class="landing-sidenav__add-section-container">
      <div class="landing-sidenav__title">
        {{ $t('add-pre-configured-section') }}
      </div>

      <div class="landing-sidenav__buttons-container">
        <div class="landing-sidenav__add-section-item" @click="batch('testimonials')">
          <i class="fal fa-quote-right fa-lg" />

          <div class="landing-sidenav__section-item-name">
            {{ $t('testimony') }}
          </div>
        </div>

        <div class="landing-sidenav__add-section-item" @click="addTabs()">
          <i class="fal fa-file-video fa-lg" />

          <div class="landing-sidenav__section-item-name">
            {{ $t('streaming') }}
          </div>
        </div>
      </div>
    </div>

    <Divider />

    <div class="landing-sidenav__add-section-container">
      <div class="landing-sidenav__title">
        {{ $t('add-section') }}
      </div>

      <div class="landing-sidenav__buttons-container">
        <div class="landing-sidenav__add-section-item" @click="addSection({ title: 'Seção de 1 coluna' })">
          <OneColumnIcon />

          <div class="landing-sidenav__section-item-name">
            {{ $t('one-column') }}
          </div>
        </div>

        <div
          class="landing-sidenav__add-section-item"
          @click="addSection({ panelCount: 2, title: 'Seção de 2 colunas' })"
        >
          <TwoColumnIcon />

          <div class="landing-sidenav__section-item-name">
            {{ $t('two-column') }}
          </div>
        </div>

        <div
          class="landing-sidenav__add-section-item"
          @click="addSection({ panelCount: 3, title: 'Seção de 3 colunas' })"
        >
          <ThreeColumnIcon />

          <div class="landing-sidenav__section-item-name">
            {{ $t('three-column') }}
          </div>
        </div>

        <div
          class="landing-sidenav__add-section-item"
          @click="addSection({ panelCount: 4, title: 'Seção de 4 colunas' })"
        >
          <FourColumnIcon />

          <div class="landing-sidenav__section-item-name">
            {{ $t('four-column') }}
          </div>
        </div>
      </div>
    </div>

    <div v-if="!hasHeroSparkBanner">
      <Divider />

      <Button icon="fal fa-plus" :label="$t('add-herospark-banner')" @click="addBanner" />
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions, mapState } from 'vuex'
import PageCompiler from '@edools/page-compiler'
import Divider from '@/shared/components/Divider'
import Button from '@/shared/components/Button'
import Content from '@/components/sidenav/shared/Content'
import Mixin from '@/components/Mixin'

import OneColumnIcon from '@/assets/page-editor/one-column.svg'
import TwoColumnIcon from '@/assets/page-editor/two-column.svg'
import ThreeColumnIcon from '@/assets/page-editor/three-column.svg'
import FourColumnIcon from '@/assets/page-editor/four-column.svg'

const { mergeWithDefaultProps } = PageCompiler
const { sections } = PageCompiler.defaults

export default {
  name: 'LandingSideNav',
  i18nOptions: { keyPrefix: 'components.sidenav.landing-side-nav' },
  components: {
    Content,
    Divider,
    Button,
    OneColumnIcon,
    TwoColumnIcon,
    ThreeColumnIcon,
    FourColumnIcon,
  },
  mixins: [Mixin],
  computed: {
    ...mapGetters('landing', ['anyActive', 'active', 'containers']),
    ...mapState('funnels', ['features']),
    hasHeroSparkBanner() {
      return this.containers(['HeroSparkBanner']).length > 0
    },
    reference() {
      if (!this.active) {
        return false
      }

      let type = this.active.type

      if (type === 'widget/Form') {
        type = 'Form'
      }
      return () => import(`@/components/sidenav/landing/${type}SideNav.vue`)
    },
  },
  methods: {
    ...mapActions('funnels', ['getFeatures']),
    ...mapActions('landing', [
      'setActive',
      'addSection',
      'addHeroSparkBanner',
      'addTabs',
      'removeSection',
      'updatePosition',
      'batchAdd',
    ]),
    ...mapActions('ui', ['scrollIntoView']),
    positionChanged({ moved }) {
      this.updatePosition({
        id: moved.element.id,
        from: moved.oldIndex,
        to: moved.newIndex,
      })
    },
    batch(type) {
      this.batchAdd({ elements: mergeWithDefaultProps(sections[type]) })
    },
    addBanner() {
      this.addHeroSparkBanner()
    },
  },
  created() {
    if (this.isFromFunnel && this.features === null) this.getFeatures()
  },
}
</script>

<style lang="scss" scoped>
.landing-sidenav {
  overflow-y: auto;
  padding: 8px;
  background-color: white;
}

.landing-sidenav__title {
  line-height: 1.5rem;
  color: $grey;
  font-weight: 700;
}

.landing-sidenav__buttons-container {
  display: grid;
  grid-template-columns: 1fr 1fr;
  grid-gap: 8px;
  margin-top: 16px;
}

.landing-sidenav__add-section-container {
  margin-bottom: 24px;
}

.landing-sidenav__add-section-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 16px;
  cursor: pointer;
  border: 1px solid $grey-20;
  border-radius: 4px;
  background-color: white;
  text-align: center;
}

.landing-sidenav__section-item-name {
  margin-top: 12px;
}
</style>
